import React, {MouseEvent, ReactElement} from "react";

import {css} from "/src/utils/utils";
import translate from "/src/utils/translate";
import {localizeSlug, buildUtmUrl} from "/src/utils/utils";
import {LOCALE} from "/src/config";

import LinkTag from "../../buttons/link-tag/LinkTag";
import Icon from "../../icon/Icon";

import useHasWindow from "/src/hooks/useHasWindow";

import MailIcon from "/src/svg/mail.svg";
import LinkIcon from "/src/svg/link.svg";
import TwitterIcon from "/src/svg/twitter-no-fill.svg";
import LinkedInIcon from "/src/svg/linkedin-no-fill.svg";
import FacebookIcon from "/src/svg/facebook-no-fill.svg";

import * as styles from "./widget-share.module.scss";

interface WidgetShareProps {
  classes?: string;
  link?: string;
  showTitle?: boolean;
}

export const URLS = {
  email: (url: string): string => {
    return `mailto:?body=${url}`;
  },
  facebook: (url: string): string => {
    return `https://www.facebook.com/sharer/sharer.php?u=${url}&amp;src=sdkpreparse`;
  },
  twitter: (url: string): string => {
    return `https://twitter.com/intent/tweet?text=${url}`;
  },
  linkedin: (url: string): string => {
    return `https://www.linkedin.com/shareArticle?mini=true&url=${url}`;
  },
};

const WidgetShare = (props: WidgetShareProps): ReactElement | null => {
  const {link, classes, showTitle} = props;

  const timeout = React.useRef<NodeJS.Timeout | null>(null);
  const [copied, setCopied] = React.useState(false);

  // this is to fix rehydration issue on static build
  const hasWindow = useHasWindow();

  const finalShareLink = () => {
    return link ? link : localizeSlug(window.location.pathname, LOCALE);
  };

  const clipboardCopy = (str: string) => {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(str)
        .then(() => {
          setCopied(true);
        })
        .catch(error => {
          console.warn(error);
        });
    }
  };

  React.useEffect(() => {
    if (timeout.current != null) {
      clearTimeout(timeout.current);
    }

    if (copied === true) {
      timeout.current = setTimeout(() => {
        setCopied(false);
      }, 2000);
    }

    return () => {
      if (timeout.current != null) {
        clearTimeout(timeout.current);
      }
    };
  }, [copied]);

  if (!hasWindow) {
    return null;
  }

  const shareContent = (
    <ul
      aria-label={translate("Share the Article")}
      className={css(styles.widgetShare__list)}
    >
      <li className={styles.widgetShare__item}>
        <LinkTag
          openInNewTab
          classes={styles.widgetShare__item__link}
          href={URLS.email(finalShareLink())}
          ariaLabel={translate("Share Page via Email")}
        >
          <Icon svg={MailIcon} classes={styles.widgetShare__item__icon} />
        </LinkTag>
      </li>
      <li className={styles.widgetShare__item}>
        <button
          aria-label={translate("Copy Page Link")}
          type="button"
          className={css(styles.widgetShare__item__link)}
          onClick={(e: MouseEvent<HTMLElement>): void => {
            clipboardCopy(buildUtmUrl(finalShareLink()));
            e.preventDefault();
          }}
        >
          <Icon svg={LinkIcon} classes={styles.widgetShare__item__icon} />
        </button>

        {copied === true && (
          <div
            role="alert"
            id="widget-share-alert"
            aria-atomic="true"
            aria-live="assertive"
            className={styles.widgetShare__item__notification}
          >
            {translate("Link copied!")}
          </div>
        )}
      </li>
      <li className={styles.widgetShare__item}>
        <LinkTag
          openInNewTab
          classes={styles.widgetShare__item__link}
          href={URLS.twitter(finalShareLink())}
          ariaLabel={translate("Share Page via Twitter")}
        >
          <Icon svg={TwitterIcon} classes={styles.widgetShare__item__icon} />
        </LinkTag>
      </li>
      <li className={styles.widgetShare__item}>
        <LinkTag
          openInNewTab
          classes={styles.widgetShare__item__link}
          href={URLS.linkedin(finalShareLink())}
          ariaLabel={translate("Share Page via LinkedIn")}
        >
          <Icon svg={LinkedInIcon} classes={styles.widgetShare__item__icon} />
        </LinkTag>
      </li>
      <li className={styles.widgetShare__item}>
        <LinkTag
          openInNewTab
          classes={styles.widgetShare__item__link}
          href={URLS.facebook(finalShareLink())}
          ariaLabel={translate("Share Page via Facebook")}
        >
          <Icon svg={FacebookIcon} classes={styles.widgetShare__item__icon} />
        </LinkTag>
      </li>
    </ul>
  );

  return (
    <div
      className={css(
        styles.widgetShare,
        showTitle ? styles.widgetShareTitle : "",
        classes!
      )}
    >
      {showTitle && (
        <p className={styles.widgetShare__title}>
          {translate("Share the Article")}
        </p>
      )}
      {shareContent}
    </div>
  );
};

export default WidgetShare;
